import { useRef, useState } from 'react';
import { extractApiErrorMessage } from '../../../../utils/extractApiErrorMessage';
import { downloadPriceArchitectureTemplate, uploadPriceArchitecture } from '../services';
import { PriceArchitectureFeedback, UsePriceArchitectureUploadResult } from '../types';

export const INVALID_EXTENSION_MESSAGE = 'Formato inválido. Envie um arquivo .xlsx, .xls ou .csv.';
export const UPLOAD_ERROR_FALLBACK_MESSAGE = [
  'Não foi possível processar o arquivo.',
  'Confira se o arquivo está em .xlsx, .xls ou .csv e tem as colunas Id Arquitetura, Produto Referência, Produto e Fator de Arquitetura preenchidas.',
  'Depois, envie novamente.',
].join(' ');

export const usePriceArchitectureUpload = (): UsePriceArchitectureUploadResult => {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<PriceArchitectureFeedback>(null);
  const isUploadingRef = useRef(false);

  const resetUpload = () => {
    setSelectedFile(null);
    setFileError(null);
  };

  const openUpload = () => {
    resetUpload();
    setIsUploadModalOpen(true);
  };

  const closeUpload = () => {
    setIsUploadModalOpen(false);
    resetUpload();
  };

  const handleFileChange = (file: File | null) => {
    setSelectedFile(file);
    setFileError(null);
  };

  const handleInvalidFile = () => {
    setSelectedFile(null);
    setFileError(INVALID_EXTENSION_MESSAGE);
  };

  const handleUpload = async () => {
    if (!selectedFile || isUploadingRef.current) return;

    isUploadingRef.current = true;
    setIsUploadModalOpen(false);
    setFeedback({ type: 'processing' });

    try {
      await uploadPriceArchitecture(selectedFile);
      setFeedback({ type: 'success' });
    } catch (error) {
      setFeedback({ type: 'error', message: extractApiErrorMessage(error) || UPLOAD_ERROR_FALLBACK_MESSAGE });
    } finally {
      isUploadingRef.current = false;
    }
  };

  const closeFeedback = () => {
    setFeedback(null);
    resetUpload();
  };

  const retryUpload = () => {
    setFeedback(null);
    setIsUploadModalOpen(true);
  };

  return {
    isUploadModalOpen,
    selectedFile,
    fileError,
    feedback,
    openUpload,
    closeUpload,
    handleFileChange,
    handleInvalidFile,
    handleDownloadTemplate: downloadPriceArchitectureTemplate,
    handleUpload,
    closeFeedback,
    retryUpload,
  };
};
